import React, {
  ReactElement,
  useRef,
  useEffect,
  useCallback,
  useState,
} from 'react';
import Slider, { Settings as SliderProps } from 'react-slick';
import styled from 'astroturf/react';

/**
 * Hook to distinguish between drag and click events in a carousel.
 * Returns handlers and a wrapped click function that only fires on true clicks.
 */
export const useDragClick = (onClick: () => void) => {
  const [isDragging, setIsDragging] = useState(false);
  const dragStartPos = useRef<{ x: number; y: number } | null>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    dragStartPos.current = { x: e.clientX, y: e.clientY };
    setIsDragging(false);
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (dragStartPos.current) {
      const deltaX = Math.abs(e.clientX - dragStartPos.current.x);
      const deltaY = Math.abs(e.clientY - dragStartPos.current.y);
      if (deltaX > 5 || deltaY > 5) {
        setIsDragging(true);
      }
    }
  }, []);

  const handleClick = useCallback(() => {
    if (!isDragging) {
      onClick();
    }
  }, [isDragging, onClick]);

  const handleMouseUp = useCallback(() => {
    dragStartPos.current = null;
  }, []);

  return {
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleClick,
  };
};

const Container = styled.div`
  @import 'stylesheets/design_system/_deprecated_grid.scss';
  position: relative;
  margin-bottom: 16px;

  @include medium-up {
    margin-bottom: 24px;
  }
`;

const SliderWrapper = styled.div`
  :global(.slick-slider) {
    position: relative;
  }

  :global(.slick-list) {
    overflow: hidden;
  }

  :global(.slick-track) {
    display: flex !important;
    align-items: stretch;
  }

  :global(.slick-slide) {
    height: auto !important;
    display: flex !important;

    > div {
      display: flex;
      width: 100%;
      height: 100%;

      > * {
        width: 100%;
      }
    }
  }

  :global(.slick-dots) {
    position: absolute;
    bottom: 16px;
    display: block !important;
    width: 100%;
    margin: 0;
    padding: 0;
    text-align: center;
    list-style: none;
    z-index: 10;

    :global(li) {
      position: relative;
      display: inline-block;
      width: 6px;
      height: 6px;
      margin: 0 3px;
      padding: 0;
      cursor: pointer;

      :global(button) {
        appearance: none;
        border: 0;
        background: rgba(177, 177, 186, 0.4);
        display: block;
        width: 6px;
        height: 6px;
        padding: 0;
        cursor: pointer;
        color: transparent;
        outline: none;
        font-size: 0;
        line-height: 0;
        border-radius: 50%;
        transition: all 0.2s ease;
      }
    }

    :global(li.slick-active) {
      width: 20px;

      :global(button) {
        width: 20px;
        border-radius: 3px;
        background: #b1b1ba;
      }
    }
  }

  :global(.slick-prev),
  :global(.slick-next) {
    font-size: 0;
    line-height: 0;
    position: absolute;
    top: 50%;
    display: block;
    width: 30px;
    height: 30px;
    padding: 0;
    transform: translate(0, -50%);
    cursor: pointer;
    color: transparent;
    border: 1px solid #dddde2;
    outline: none;
    background: white;
    border-radius: 50%;
    box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.08);
    z-index: 2;

    &:global(.slick-disabled) {
      opacity: 0.3;
      cursor: not-allowed;
    }

    &:hover {
      opacity: 0.8;
    }
  }

  :global(.slick-prev) {
    left: 10px;

    &:before {
      content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'%3E%3Cpath d='M12.5 15L7.5 10L12.5 5' stroke='%23535366' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    }
  }

  :global(.slick-next) {
    right: 10px;

    &:before {
      content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'%3E%3Cpath d='M7.5 15L12.5 10L7.5 5' stroke='%23535366' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    }
  }
`;

type Props = {
  items: ReactElement[];
  autoplay?: boolean;
  autoplaySpeed?: number;
};

const DotButton: React.FC = () => {
  // Using native button instead of design system Button for carousel dots
  // eslint-disable-next-line no-restricted-syntax
  return <button type="button" />;
};

/**
 * A single-page marketing carousel component that displays one item at a time
 * with auto-rotation support and navigation arrows.
 *
 * Props:
 * - `items`: Array of React elements to display in the carousel
 * - `autoplay`: Enable auto-rotation (default: true)
 * - `autoplaySpeed`: Speed of auto-rotation in milliseconds (default: 5000)
 *
 * @example
 * ```tsx
 * <MarketingCarousel
 *   items={[
 *     <PodcastLibraryCarouselAd key="podcast" />,
 *     <AnotherMarketingItem key="other" />
 *   ]}
 * />
 * ```
 */
export const MarketingCarousel: React.FC<Props> = ({
  items,
  autoplay = true,
  autoplaySpeed = 5000,
}) => {
  const sliderRef = useRef<Slider>(null);

  const customPaging = useCallback(() => <DotButton />, []);

  const settings: SliderProps = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay,
    autoplaySpeed,
    arrows: true,
    pauseOnHover: true,
    customPaging,
  };

  // Pause autoplay when tab is not visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        sliderRef.current?.slickPause();
      } else {
        sliderRef.current?.slickPlay();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  if (items.length === 0) {
    return null;
  }

  // If only one item, don't show carousel controls
  if (items.length === 1) {
    return <Container>{items[0]}</Container>;
  }

  return (
    <Container>
      <SliderWrapper>
        <Slider ref={sliderRef} {...settings}>
          {items}
        </Slider>
      </SliderWrapper>
    </Container>
  );
};
